import { createTheme, Theme } from '@mui/material'
import { createChart, IChartApi, ISeriesApi, LineData, LineType, LogicalRange, MouseEventParams, SeriesType, Time } from 'lightweight-charts'
import ChartDataManager from './chartDataManager'
import CombinedLegSeriesManager, {
    OI_DIFF_SERIES_CONFIG,
    STRADDLE_SERIES_CONFIG,
} from './combinedLegSeries'
import { SuperChartConfiguration_DateRange, SuperChartConfiguration_TimeBucket } from '../../proto/generated/superchart-page'
import { optionTypeToJSON, Security } from '../../proto/generated/security'
import { constructCalendarDataLookup, DataUpdateRequestType, getNextChartTimeStamps, getTimeIndex, getViewportCandleInfo, ViewportCandleInfo } from '../../util/chart'
import { convertToTimezone, istToUTC, TimeZone, toDayFormat } from '../../util/dateTime'
import { calculateCandlesBetweenDates, zonedTime } from '../../util/chartUtils'
import { CallPutType, MultiStrikeChartStylesTypes, MultiStrikeChartTypes, MultiStrikeSecuritiesClassType } from '../../reducer/multiStrikeChart.reducer'
import { WebSocketContextValue } from '../webSocketConnection/WebSocket'
import { debounce, throttle } from 'throttle-debounce'
import { INITIAL_CANDLE_COUNT } from '../../assets/constants'
import { TimeBucket_TimeSeries } from '../../proto/generated/common'
import { primarySecColorDarkTheme, primarySecColorLightTheme } from './styles'
import { getMultiStrikeCallColor, getMultiStrikePutColor, resolveMultiStrikeColorPalette } from './multiStrikeColorPalettes'
import { ChartCandle, ChartLayoutOptions, ScalesAndLinesSettings, SeriesData } from '../chartV2/chart.typings'
import { getTimeWidthInMilliSeconds } from '../chartV2/chartHelpers'
import { ChartSnapshotMultiStrikeChart, CrossHairInfoMultiStrikeChart } from './chart.typings'
import LineSeries from './series/lineSeries'
import { getCumLineData } from './chartHelpers'
import { TradingCalendarData } from '../../proto/generated/trading-calendar'
import { getDesignTokens } from '../../theme/theme'
import { getOverlayPriceScaleStyleOptions, OverlayPriceScale } from '../chartV2/chartWrapper/overlay-price-scale.ts'
import { applyCrosshairToMultiStrikeMetadataDom } from './multiStrikeChartMetadataDom'
import { formatLargeNumber } from '../../util/screener'
import { canvasLineStyleToLineStyle } from '../../util/canvasSettingsDefaults'
import { defaultScalesAndLinesSettings } from '../../util/scalesAndLinesDefaults'

const INITIAL_VIEWPORT_RIGHT_SLOT = 5

const LEFT_SCALE_PRICE_FORMAT = {
    type: 'custom' as const,
    formatter: (price: number) =>
        price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    minMove: 0.01
}

const RIGHT_SCALE_PRICE_FORMAT = {
    type: 'custom' as const,
    formatter: (price: number) => formatLargeNumber(price, 2),
    minMove: 0.01
}

export type ChartOptions = {
    id: string
    viewKey: string
    container: HTMLDivElement
    primary: string
    timeBucket: SuperChartConfiguration_TimeBucket
    timeBuckets: SuperChartConfiguration_TimeBucket[]
    dateRange: SuperChartConfiguration_DateRange | undefined
    gotoDate?: number
    viewportCandleInfo?: ViewportCandleInfo
    secondarySecurities: MultiStrikeSecuritiesClassType
    callPutSelected: CallPutType[]
    chartType: MultiStrikeChartTypes
    showChange: boolean
    cumOi?: boolean
    tradingCalendarData: TradingCalendarData | undefined
    theme: 'dark' | 'light'
    chartStyles: MultiStrikeChartStylesTypes
    scalesAndLinesSettings?: ScalesAndLinesSettings
    emptyTimeslotsSize: number
    layoutOptions: ChartLayoutOptions
    timezone: TimeZone
    subscriptionTag: string
    persistViewport?: boolean
    ws: WebSocketContextValue
    isEmbedded?: boolean
} & ChartCallbacks

export type ChartCallbacks = {
    onDataUpdate: () => void
    onCrosshairMove: (event?: CrossHairInfoMultiStrikeChart) => void
    onLoading?: (loading: boolean) => void
    onTimeRangeChange?: (viewportCandleInfo: ViewportCandleInfo, gotoDate?: number) => void
    onTimeRangeChangeHistory?: (ViewportCandleInfo: ViewportCandleInfo) => void
    onMouseEnter?: () => void
    onMouseMove?: () => void // Listener for mouse movement within the container
    onMouseLeave?: () => void
}

export default class Chart {
    id: string
    viewKey: string
    container: HTMLDivElement
    primary: string
    timeBucket: SuperChartConfiguration_TimeBucket
    timeBuckets: SuperChartConfiguration_TimeBucket[]
    gotoDate?: number
    persistViewport = false
    dateRange: SuperChartConfiguration_DateRange | undefined
    secondarySecurities: MultiStrikeSecuritiesClassType
    callPutSelected: CallPutType[]
    chartType: MultiStrikeChartTypes
    showChange: boolean
    cumOi = false
    theme: 'dark' | 'light'
    readonly overlayMuiTheme: Theme
    chartStyles: MultiStrikeChartStylesTypes
    scalesAndLinesSettings: ScalesAndLinesSettings
    timezone: TimeZone
    ws: WebSocketContextValue
    subscriptionTag: string
    chartApi!: IChartApi
    destroyed = false
    silentUpdateFromHistory: boolean = false
    focused = false
    isEmbedded = false
    viewportCandleInfo: ViewportCandleInfo = {
        barsBefore: 0,
        barsAfter: 0,
        rangeStart: 0,
        rangeEnd: 0,
        startTime: 0,
        endTime: 0,
        viewportCandles: 0
    }
    previousViewPortCandleInfo: ViewportCandleInfo = {
        barsBefore: 0,
        barsAfter: 0,
        rangeStart: 0,
        rangeEnd: 0,
        startTime: 0,
        endTime: 0,
        viewportCandles: 0
    }
    layoutOptions: ChartLayoutOptions
    currentCrosshairOffset = 0
    crossHairInfo?: CrossHairInfoMultiStrikeChart
    holidays: Set<string>
    tradingCalendarData: TradingCalendarData | undefined
    emptyTimeslotsSize: number
    emptyTimeSlots: any[] = []
    primaryTimes: Set<Time> = new Set()
    containerBounds?: DOMRect
    hoverActive = false
    dataManager: ChartDataManager
    straddleSeries: CombinedLegSeriesManager
    oiDiffSeries: CombinedLegSeriesManager
    private _mouseEntered = false
    private _syncCrosshairActive = false
    isDateRangeTemporary: boolean = false
    notifyGoToDateToChart: boolean = false
    resizeObserver: ResizeObserver

    callbacks: ChartCallbacks = {
        onDataUpdate: () => {},
        onCrosshairMove: (event?: CrossHairInfoMultiStrikeChart) => {}
    }

    constructor(options: ChartOptions) {
        this.id = options.id
        this.viewKey = options.viewKey
        this.container = options.container
        this.resizeObserver = new ResizeObserver(() => {
            this.containerBounds = this.container.getBoundingClientRect()
        })
        this.resizeObserver.observe(this.container)
        this.primary = options.primary
        this.timeBucket = options.timeBucket
        this.timeBuckets = options.timeBuckets
        this.dateRange = options.dateRange
        this.persistViewport = options.persistViewport || false
        this.secondarySecurities = options.secondarySecurities
        this.callPutSelected = options.callPutSelected
        this.chartType = options.chartType
        this.showChange = options.showChange
        this.cumOi = options.cumOi ?? false
        this.theme = options.theme
        this.overlayMuiTheme = createTheme(getDesignTokens(this.theme))
        this.chartStyles = options.chartStyles
        this.scalesAndLinesSettings = {
            ...defaultScalesAndLinesSettings,
            ...(options.scalesAndLinesSettings ?? {}),
        }
        this.timezone = options.timezone
        this.layoutOptions = options.layoutOptions
        this.ws = options.ws
        this.subscriptionTag = options.subscriptionTag
        this.isEmbedded = options.isEmbedded || false
        this.holidays = new Set(options.tradingCalendarData?.holidays?.map((holiday) => holiday.date))
        this.tradingCalendarData = options.tradingCalendarData
        this.emptyTimeslotsSize = options.emptyTimeslotsSize
        this.callbacks.onDataUpdate = options.onDataUpdate
        this.callbacks.onCrosshairMove = options.onCrosshairMove

        if (options.gotoDate) {
            this.gotoDate = options.gotoDate
        }

        if (options.viewportCandleInfo) {
            this.viewportCandleInfo = options.viewportCandleInfo
            this.previousViewPortCandleInfo = options.viewportCandleInfo
        }

        if (options.onLoading) {
            this.callbacks.onLoading = options.onLoading
        }
        if (options.onTimeRangeChange) {
            this.callbacks.onTimeRangeChange = options.onTimeRangeChange
        }
        if (options.onTimeRangeChangeHistory) {
            this.callbacks.onTimeRangeChangeHistory = options.onTimeRangeChangeHistory
        }
        if (options.onMouseEnter) {
            this.callbacks.onMouseEnter = options.onMouseEnter
        }
        if (options.onMouseMove) {
            this.callbacks.onMouseMove = options.onMouseMove
        }
        if (options.onMouseLeave) {
            this.callbacks.onMouseLeave = options.onMouseLeave
        }

        this.dataManager = new ChartDataManager({
            ...options,
            chart: this
        })
        this.straddleSeries = new CombinedLegSeriesManager(this, STRADDLE_SERIES_CONFIG)
        this.oiDiffSeries = new CombinedLegSeriesManager(this, OI_DIFF_SERIES_CONFIG)

        this.chartApi = createChart(this.container, {
            ...this.layoutOptions,
            height: this.container.clientHeight,
            width: this.container.clientWidth,
            autoSize: true,
            leftPriceScale: {
                ...this.layoutOptions.leftPriceScale,
                visible: !this.isEmbedded && (this.layoutOptions.leftPriceScale?.visible ?? true),
                borderVisible: false
            },
            rightPriceScale: {
                ...this.layoutOptions.rightPriceScale,
                visible: !this.isEmbedded,
                borderVisible: false
            }
        })
        this.initialize()
    }

    initialize() {
        this.fetchData(true, true).finally(() => {
            if (this.destroyed || !this.chartApi) return
            this.chartApi.subscribeCrosshairMove(this.onCrosshairMove)
            this.containerBounds = this.container.getBoundingClientRect()
            document.addEventListener('mousemove', this.onMouseMove)
        })
    }

    reinitialize() {
        this.fetchData(true, true)
    }

    updateTradingCalendarData(newTradingCalendardata: TradingCalendarData) {
        this.tradingCalendarData = newTradingCalendardata
        this.holidays = new Set(newTradingCalendardata.holidays.map((holiday) => holiday.date))
    }

    updateTimeRangeChangeListener = (listener: (viewportCandleInfo: ViewportCandleInfo) => void) => {
        this.callbacks.onTimeRangeChange = listener
    }

    async fetchData(initialize: boolean, updateConfig = false) {
        if (initialize) {
            !this.gotoDate && this.resetViewportCandleInfo()
        }
        let noOfCandleRequests = await this.dataManager.fetchData({ initial: initialize, updateConfig: updateConfig })
        if (initialize) {
            this.onCrosshairMove({} as MouseEventParams<Time>)
        }
        return noOfCandleRequests
    }

    resolveUnderlyingStyle() {
        const defaultColor = this.theme === 'dark' ? primarySecColorDarkTheme : primarySecColorLightTheme
        return {
            color: this.chartStyles.underlyingColor ?? defaultColor,
            lineWidth: (this.chartStyles.underlyingThickness ?? this.chartStyles.lineThickness ?? 1) as any,
            lineStyle: canvasLineStyleToLineStyle(this.chartStyles.underlyingLineStyle ?? 'large_dashed'),
            lineType: this.chartStyles.underlyingLineType ?? LineType.Simple
        }
    }

    resolveStrikeLineStyle() {
        return canvasLineStyleToLineStyle(this.chartStyles.strikeLineStyle ?? 'solid')
    }

    resolveUnderlyingScaleOptions() {
        const settings = this.scalesAndLinesSettings
        return {
            priceLineVisible: settings.showSymbolLine ?? true,
            lastValueVisible: settings.showSymbolValue ?? true,
        }
    }

    resolveStrikeScaleOptions(title: string) {
        const settings = this.scalesAndLinesSettings
        const showLabel = settings.showIndicatorLabel ?? true
        const showValue = settings.showIndicatorValue ?? true
        return {
            title: showLabel ? title : '',
            lastValueVisible: showValue,
        }
    }

    getStrikeSeriesTitle(key: string): string {
        const arr = key.split('_')
        const security = window.securityManager.getSecurity(key)
        const strikePrice = security?.strikePrice ?? Number(arr[arr.length - 2])
        const optionType = security?.optionType ? optionTypeToJSON(security.optionType) : arr[arr.length - 1]
        const priceMultiplier = window.securityManager.getSecurity(this.primary)?.priceMultiplier ?? 100
        return `${(strikePrice ?? 0) / priceMultiplier} ${optionType}`
    }

    applyScalesAndLinesSettings() {
        if (!this.chartApi || this.destroyed) return
        const alignLabels = this.scalesAndLinesSettings.alignLabels ?? true
        this.chartApi.applyOptions({
            rightPriceScale: { alignLabels },
            leftPriceScale: { alignLabels },
        })
        this.dataManager.seriesApis[this.primary]?.updateOptions(this.resolveUnderlyingScaleOptions())
        Object.keys(this.dataManager.seriesApis).forEach((key) => {
            if (key === this.primary) return
            const title = this.getStrikeSeriesTitle(key)
            this.dataManager.seriesApis[key]?.updateOptions(this.resolveStrikeScaleOptions(title))
        })
        this.dataManager.cumSeriesApis['cum_oi_CE']?.updateOptions(this.resolveStrikeScaleOptions('CE'))
        this.dataManager.cumSeriesApis['cum_oi_PE']?.updateOptions(this.resolveStrikeScaleOptions('PE'))
        this.dataManager.cumSeriesApis['cum_straddle']?.updateOptions(this.resolveStrikeScaleOptions('STR'))
        this.dataManager.cumSeriesApis['cum_oi_diff']?.updateOptions(this.resolveStrikeScaleOptions('DIFF'))
        this.straddleSeries.applyScaleAndStyleOptions()
        this.oiDiffSeries.applyScaleAndStyleOptions()
    }

    applyNewStyles() {
        const underlyingStyle = this.resolveUnderlyingStyle()
        const strikeLineStyle = this.resolveStrikeLineStyle()
        Object.keys(this.dataManager.data).forEach((d) => {
            if (this.dataManager.seriesApis[d]) {
                if (d === this.primary) {
                    this.dataManager.seriesApis[d]?.updateOptions({
                        ...underlyingStyle
                    })
                } else {
                    this.dataManager.seriesApis[d]?.updateOptions({
                        lineType: this.chartStyles.lineType,
                        lineWidth: this.chartStyles.lineThickness as any,
                        lineStyle: strikeLineStyle
                    })
                }
            }
        })
        this.dataManager.cumSeriesApis['cum_oi_CE']?.updateOptions({
            lineType: this.chartStyles.lineType,
            lineWidth: this.chartStyles.lineThickness as any,
            lineStyle: strikeLineStyle
        })
        this.dataManager.cumSeriesApis['cum_oi_PE']?.updateOptions({
            lineType: this.chartStyles.lineType,
            lineWidth: this.chartStyles.lineThickness as any,
            lineStyle: strikeLineStyle
        })
        this.straddleSeries.applyScaleAndStyleOptions()
        this.oiDiffSeries.applyScaleAndStyleOptions()
        this.applySeriesPaletteColors()
    }

    applySeriesPaletteColors() {
        const palette = resolveMultiStrikeColorPalette(this.chartStyles, this.theme)
        Object.keys(this.secondarySecurities).forEach((strike, index) => {
            const callColor = getMultiStrikeCallColor(this.chartStyles, index, this.theme)
            const putColor = getMultiStrikePutColor(this.chartStyles, index, this.theme)
            this.dataManager.seriesApis[this.secondarySecurities[strike].callSecKey]?.updateOptions({
                color: callColor,
                priceLineColor: callColor + '80'
            })
            this.dataManager.seriesApis[this.secondarySecurities[strike].putSecKey]?.updateOptions({
                color: putColor,
                priceLineColor: putColor + '80'
            })
        })
        this.dataManager.cumSeriesApis['cum_oi_CE']?.updateOptions({
            color: palette.callCumulative,
            priceLineColor: palette.callCumulative + '80'
        })
        this.dataManager.cumSeriesApis['cum_oi_PE']?.updateOptions({
            color: palette.putCumulative,
            priceLineColor: palette.putCumulative + '80'
        })
        this.straddleSeries.applyPaletteColors()
        this.oiDiffSeries.applyPaletteColors()
    }

    updateStrikeCallPutVisible(secondarySecurities: MultiStrikeSecuritiesClassType) {
        Object.keys(secondarySecurities).forEach((strike) => {
            const callKey = secondarySecurities[strike].callSecKey
            const putKey = secondarySecurities[strike].putSecKey
            const callPutSelected = secondarySecurities[strike].callPut

            this.dataManager.seriesApis[callKey]?.updateOptions({
                visible: callPutSelected.includes('CE')
            })

            this.dataManager.seriesApis[putKey]?.updateOptions({
                visible: callPutSelected.includes('PE')
            })
        })
    }

    updateCumOiCallPutVisible() {
        this.dataManager.cumSeriesApis['cum_oi_CE']?.updateOptions({
            visible: this.callPutSelected.includes('CE')
        })

        this.dataManager.cumSeriesApis['cum_oi_PE']?.updateOptions({
            visible: this.callPutSelected.includes('PE')
        })
        this.onCrosshairMove({} as any)
    }

    private ensureVisibleSeries() {
        const hasVisibleSeries = this.hasAnyVisibleSeries()
        if (!hasVisibleSeries && this.dataManager.seriesApis[this.primary]) {
            console.warn('No visible series detected, ensuring primary series is visible')
        }
    }

    private hasAnyVisibleSeries(): boolean {
        if (this.dataManager.seriesApis[this.primary]?.options?.visible !== false) {
            return true
        }

        for (const seriesKey of Object.keys(this.dataManager.seriesApis)) {
            if (seriesKey !== this.primary && this.dataManager.seriesApis[seriesKey]?.options?.visible === true) {
                return true
            }
        }

        for (const cumKey of Object.keys(this.dataManager.cumSeriesApis)) {
            if (this.dataManager.cumSeriesApis[cumKey]?.options?.visible === true) {
                return true
            }
        }

        for (const series of Object.values(this.straddleSeries.byStrike)) {
            if (series?.options?.visible === true) {
                return true
            }
        }

        for (const series of Object.values(this.oiDiffSeries.byStrike)) {
            if (series?.options?.visible === true) {
                return true
            }
        }

        return false
    }

    setDataInSeriesApi(seriesApi: ISeriesApi<'Line', Time> | undefined, requestType: DataUpdateRequestType, data: any[]) {
        if (seriesApi) {
            if (requestType === DataUpdateRequestType.LIVE) {
                seriesApi.update(data[data.length - 1])
            } else {
                seriesApi.setData(data)
            }
        }
    }

    setDataInLineSeries(series: LineSeries, requestType: DataUpdateRequestType, data: ChartCandle[]) {
        if (requestType === DataUpdateRequestType.LIVE) {
            const [candle] = data.slice(-1)
            series._update(candle)
        } else {
            series._setData(data)
        }
    }

    async onDataLoaded(requestType: DataUpdateRequestType, newDataFetched: boolean, updateConfig = false, resetTimeScale = false, changedKeys?: Set<string>) {
        if (this.destroyed || !this.chartApi) return
        if (requestType !== DataUpdateRequestType.LIVE) {
            this.unsubscribeVisibleLogicalRange()
        }
        this.updatePrimaryTimesAndEmptyslots()
        if (this.dataManager.blankSeriesApi) {
            this.setDataInSeriesApi(this.dataManager.blankSeriesApi, requestType, this.emptyTimeSlots)
        } else {
            this.dataManager.blankSeriesApi = this.chartApi.addLineSeries({ priceFormat: RIGHT_SCALE_PRICE_FORMAT })
            this.setDataInSeriesApi(this.dataManager.blankSeriesApi, DataUpdateRequestType.INITIAL, this.emptyTimeSlots)
        }
        const keysToUpdate = requestType === DataUpdateRequestType.LIVE && changedKeys?.size
            ? Object.keys(this.dataManager.data).filter((d) => changedKeys.has(d))
            : Object.keys(this.dataManager.data)

        keysToUpdate.forEach((d) => {
            if (!this.dataManager.seriesApis[d]) {
                if (d === this.primary) {
                    this.dataManager.seriesApis[d] = new LineSeries(this, {
                        priceScaleId: 'left',
                        ...this.resolveUnderlyingStyle(),
                        ...this.resolveUnderlyingScaleOptions(),
                        crosshairMarkerRadius: 3,
                        source: 'close',
                        priceFormat: LEFT_SCALE_PRICE_FORMAT
                    })
                    // Add OverlayPriceScale for primary series (left side) in embedded mode
                    if (this.isEmbedded) {
                        this.dataManager.seriesApis[d].seriesApi.attachPrimitive(
                            new OverlayPriceScale({ side: 'left', ...getOverlayPriceScaleStyleOptions(this.overlayMuiTheme) })
                        )
                    }
                } else {
                    const fullTitle = this.getStrikeSeriesTitle(d)
                    this.dataManager.seriesApis[d] = new LineSeries(this, {
                        priceScaleId: 'right',
                        ...this.resolveStrikeScaleOptions(fullTitle),
                        lineType: this.chartStyles.lineType,
                        lineWidth: this.chartStyles.lineThickness as any,
                        lineStyle: this.resolveStrikeLineStyle(),
                        crosshairMarkerRadius: 3,
                        priceFormat: RIGHT_SCALE_PRICE_FORMAT
                    })
                    // Add OverlayPriceScale for secondary series (right side) in embedded mode
                    if (this.isEmbedded) {
                        this.dataManager.seriesApis[d].seriesApi.attachPrimitive(
                            new OverlayPriceScale({ side: 'right', ...getOverlayPriceScaleStyleOptions(this.overlayMuiTheme) })
                        )
                    }
                }
            }
            if (d === this.primary) {
                this.setDataInLineSeries(this.dataManager.seriesApis[d], requestType, this.dataManager.data[d])
            } else {
                switch (this.chartType) {
                    case 'oi': {
                        if (this.dataManager.seriesApis[d].options.source !== 'openInterest') {
                            this.dataManager.seriesApis[d].updateOptions({
                                source: 'openInterest'
                            })
                        }
                        this.setDataInLineSeries(this.dataManager.seriesApis[d], requestType, this.dataManager.data[d])
                        break
                    }
                    case 'oiChange': {
                        if (this.dataManager.seriesApis[d].options.source !== 'series') {
                            this.dataManager.seriesApis[d].updateOptions({
                                source: 'series'
                            })
                        }
                        this.setDataInLineSeries(this.dataManager.seriesApis[d], requestType, this.dataManager.data[d])
                        break
                    }
                    case 'price': {
                        if (this.dataManager.seriesApis[d].options.source !== 'close') {
                            this.dataManager.seriesApis[d].updateOptions({
                                source: 'close'
                            })
                        }
                        this.setDataInLineSeries(this.dataManager.seriesApis[d], requestType, this.dataManager.data[d])
                        break
                    }
                    case 'iv': {
                        if (this.dataManager.seriesApis[d].options.source !== 'impliedVolatility') {
                            this.dataManager.seriesApis[d].updateOptions({
                                source: 'impliedVolatility'
                            })
                        }
                        this.setDataInLineSeries(this.dataManager.seriesApis[d], requestType, this.dataManager.data[d])
                        break
                    }
                    case 'cumOi':
                    case 'straddle':
                    case 'oiDiff': {
                        if (d !== this.primary) {
                            this.dataManager.seriesApis[d]?.updateOptions({
                                visible: false
                            })
                        }
                        break
                    }
                }
            }
        })

        if (this.chartType !== 'cumOi' && this.chartType !== 'straddle' && this.chartType !== 'oiDiff') {
            this.updateStrikeCallPutVisible(this.secondarySecurities)
        }

        if (requestType !== DataUpdateRequestType.LIVE) {
            this.applySeriesPaletteColors()
            if (!this.dataManager.cumSeriesApis['cum_oi_CE']) {
                this.dataManager.cumSeriesApis['cum_oi_CE'] = new LineSeries(this, {
                    priceScaleId: 'right',
                    priceLineColor: resolveMultiStrikeColorPalette(this.chartStyles, this.theme).callCumulative + '80',
                    color: resolveMultiStrikeColorPalette(this.chartStyles, this.theme).callCumulative,
                    visible: this.callPutSelected.includes('CE'),
                    ...this.resolveStrikeScaleOptions('CE'),
                    lineType: this.chartStyles.lineType,
                    lineWidth: this.chartStyles.lineThickness as any,
                    lineStyle: this.resolveStrikeLineStyle(),
                    crosshairMarkerRadius: 3,
                    source: 'openInterest',
                    priceFormat: RIGHT_SCALE_PRICE_FORMAT
                })
                // Add OverlayPriceScale for cumulative CE series (right side) in embedded mode
                if (this.isEmbedded) {
                    this.dataManager.cumSeriesApis['cum_oi_CE'].seriesApi.attachPrimitive(
                        new OverlayPriceScale({ side: 'right', ...getOverlayPriceScaleStyleOptions(this.overlayMuiTheme) })
                    )
                }
            }

            if (!this.dataManager.cumSeriesApis['cum_oi_PE']) {
                this.dataManager.cumSeriesApis['cum_oi_PE'] = new LineSeries(this, {
                    priceScaleId: 'right',
                    priceLineColor: resolveMultiStrikeColorPalette(this.chartStyles, this.theme).putCumulative + '80',
                    color: resolveMultiStrikeColorPalette(this.chartStyles, this.theme).putCumulative,
                    visible: this.callPutSelected.includes('PE'),
                    ...this.resolveStrikeScaleOptions('PE'),
                    lineType: this.chartStyles.lineType,
                    lineWidth: this.chartStyles.lineThickness as any,
                    lineStyle: this.resolveStrikeLineStyle(),
                    crosshairMarkerRadius: 3,
                    source: 'openInterest',
                    priceFormat: RIGHT_SCALE_PRICE_FORMAT
                })
                // Add OverlayPriceScale for cumulative PE series (right side) in embedded mode
                if (this.isEmbedded) {
                    this.dataManager.cumSeriesApis['cum_oi_PE'].seriesApi.attachPrimitive(
                        new OverlayPriceScale({ side: 'right', ...getOverlayPriceScaleStyleOptions(this.overlayMuiTheme) })
                    )
                }
            }

            const callKeys = Object.keys(this.secondarySecurities).map((s) => this.secondarySecurities[s].callSecKey)
            const putKeys = Object.keys(this.secondarySecurities).map((s) => this.secondarySecurities[s].putSecKey)

            const callOiDataArray = callKeys.map((k) => {
                return this.dataManager.data[k] ?? []
            })

            const putOiDataArray = putKeys.map((k) => {
                return this.dataManager.data[k]
            })
            this.dataManager.cumSeriesApis['cum_oi_CE']._setData(getCumLineData(callOiDataArray) as any)
            this.dataManager.cumSeriesApis['cum_oi_PE']._setData(getCumLineData(putOiDataArray) as any)
        }

        if (this.chartType === 'cumOi') {
            const cumOiSource = this.showChange ? 'series' : 'openInterest'
            this.dataManager.cumSeriesApis['cum_oi_CE']?.updateOptions({
                visible: this.callPutSelected.includes('CE'),
                source: cumOiSource
            })
            this.dataManager.cumSeriesApis['cum_oi_PE']?.updateOptions({
                visible: this.callPutSelected.includes('PE'),
                source: cumOiSource
            })
        } else {
            this.dataManager.cumSeriesApis['cum_oi_CE']?.updateOptions({
                visible: false
            })
            this.dataManager.cumSeriesApis['cum_oi_PE']?.updateOptions({
                visible: false
            })
        }

        // Rebuild straddle lines from leg candles already in memory (also on chart-type switch).
        this.straddleSeries.refresh()
        this.oiDiffSeries.refresh()

        if (resetTimeScale || requestType === DataUpdateRequestType.INITIAL) {
            this.resetTimeScale()
        }

        this.callbacks.onDataUpdate()

        setTimeout(() => {
            if (this.destroyed || !this.chartApi) return
            if (requestType !== DataUpdateRequestType.LIVE) this.subscribeVisibleLogicalRange()
            const primarySeries = this.dataManager.seriesApis[this.primary]?.seriesApi
            if (!primarySeries) return
            const range = this.chartApi.timeScale().getVisibleLogicalRange()

            if (!range) return
            const barsInfo = primarySeries.barsInLogicalRange(range)
            if (!barsInfo) return
            this.dataManager.setDataLoading(false)
            requestType !== DataUpdateRequestType.LIVE && this.callbacks.onLoading && this.callbacks.onLoading(false)
            this.setViewportCandleInfo(getViewportCandleInfo(range, barsInfo, this.getRenderedPrimaryData()))
            this.ensureVisibleSeries()
            if (!this._mouseEntered) this.onCrosshairMove({} as any)
            this.callbacks.onDataUpdate()
        }, 500)

        if (requestType === DataUpdateRequestType.LIVE && changedKeys?.size) {
            this.notifyLiveDataUpdate(changedKeys)
        }
    }

    private notifyLiveDataUpdate(changedKeys: Set<string>) {
        if (this._mouseEntered) return
        const seriesData = new Map(this.crossHairInfo?.seriesData ?? [])
        changedKeys.forEach((key) => {
            const series = this.dataManager.seriesApis[key]
            const data = series?.data
            if (series && data?.length) {
                seriesData.set(series.seriesApi, data[data.length - 1])
            }
        })
        const secondaryChanged = [...changedKeys].some((k) => k !== this.primary)
        if (this.chartType === 'cumOi' && secondaryChanged) {
            ;['cum_oi_CE', 'cum_oi_PE'].forEach((cumKey) => {
                const series = this.dataManager.cumSeriesApis[cumKey]
                const data = series?.data
                if (series && data?.length) {
                    seriesData.set(series.seriesApi, data[data.length - 1])
                }
            })
        }
        if (this.chartType === 'straddle' && secondaryChanged) {
            if (this.cumOi) {
                const series = this.dataManager.cumSeriesApis['cum_straddle']
                const data = series?.data
                if (series && data?.length) {
                    seriesData.set(series.seriesApi, data[data.length - 1])
                }
            } else {
                Object.values(this.straddleSeries.byStrike).forEach((series) => {
                    const data = series?.data
                    if (series && data?.length) {
                        seriesData.set(series.seriesApi, data[data.length - 1])
                    }
                })
            }
        }
        if (this.chartType === 'oiDiff' && secondaryChanged) {
            if (this.cumOi) {
                const series = this.dataManager.cumSeriesApis['cum_oi_diff']
                const data = series?.data
                if (series && data?.length) {
                    seriesData.set(series.seriesApi, data[data.length - 1])
                }
            } else {
                Object.values(this.oiDiffSeries.byStrike).forEach((series) => {
                    const data = series?.data
                    if (series && data?.length) {
                        seriesData.set(series.seriesApi, data[data.length - 1])
                    }
                })
            }
        }
        this.crossHairInfo = {
            ...(this.crossHairInfo ?? { point: undefined as any, time: 0 as any, price: undefined as any, seriesData: new Map() }),
            seriesData
        }
        applyCrosshairToMultiStrikeMetadataDom(this.viewKey, this.crossHairInfo)
    }

    private publishMetadataCrosshair() {
        applyCrosshairToMultiStrikeMetadataDom(this.viewKey, this.crossHairInfo)
    }

    setFocused(focused: boolean) {
        this.focused = focused
    }

    updateCrossHairMoveCallback(onCrosshairMove: (event?: CrossHairInfoMultiStrikeChart) => void) {
        this.callbacks.onCrosshairMove = onCrosshairMove
    }

    resetTimeScale() {
        if (this.destroyed || !this.chartApi) return
        const primarySeries = this.dataManager.seriesApis[this.primary]
        this.chartApi.priceScale('left').applyOptions({ autoScale: true })
        this.chartApi.priceScale('right').applyOptions({ autoScale: true })
        if (!primarySeries) {
            this.dataManager.setDataLoading(false)
            this.callbacks.onLoading && this.callbacks.onLoading(false)
            return
        }

        const data = this.getRenderedPrimaryData()
        let candlesLength = data.length
        if (!candlesLength) {
            this.dataManager.setDataLoading(false)
            this.callbacks.onLoading && this.callbacks.onLoading(false)
            return
        }
        let from = Math.max(candlesLength - INITIAL_CANDLE_COUNT, 0)

        if (this.dateRange) {
            const selectedDateRange = this.dateRange
            const dateRange: [Date, Date] = [new Date(selectedDateRange.startTime), new Date(selectedDateRange.endTime || Date.now())]
            const dateRangeCandleCount = Math.ceil(calculateCandlesBetweenDates(dateRange, { timeSeries: this.timeBucket.timeSeries, value: this.timeBucket.timeValue }))
            const index = Math.max(candlesLength - dateRangeCandleCount, 0)

            if (candlesLength > dateRangeCandleCount && index) {
                from = index
            } else {
                from = -3
            }
            if (this.isDateRangeTemporary) {
                this.dateRange = undefined
                this.isDateRangeTemporary = false
            }
        }
        const fromTime = data[from]?.time
        const toTime = this.emptyTimeSlots?.[INITIAL_VIEWPORT_RIGHT_SLOT]?.time ?? data[candlesLength - 1].time
        if (fromTime && toTime) {
            if (this.gotoDate) {
                this.focusTimeInViewPort(this.gotoDate, data)
            } else {
                this.setVisibleRange(fromTime as Time, toTime as Time)
            }

            if ([TimeBucket_TimeSeries.HOUR, TimeBucket_TimeSeries.MINUTE, TimeBucket_TimeSeries.SECOND].indexOf(this.timeBucket.timeSeries) !== -1) {
                this.chartApi.timeScale().applyOptions({
                    timeVisible: true,
                    secondsVisible: true
                })
            } else {
                this.chartApi.timeScale().applyOptions({
                    timeVisible: false,
                    secondsVisible: false
                })
            }
        }

        setTimeout(() => {
            if (this.destroyed || !this.chartApi) return
            this.dataManager.setDataLoading(false)
            this.callbacks.onLoading && this.callbacks.onLoading(false)
        }, 50)
    }

    subscribeVisibleLogicalRange() {
        if (this.destroyed || !this.chartApi) return
        this.chartApi.timeScale().subscribeVisibleLogicalRangeChange(this.onVisibleRangeChange)
    }

    unsubscribeVisibleLogicalRange() {
        if (this.destroyed || !this.chartApi) return
        this.chartApi.timeScale().unsubscribeVisibleLogicalRangeChange(this.onVisibleRangeChange)
    }

    private buildSeriesDataAtTime(time: number) {
        const seriesData = new Map<ISeriesApi<SeriesType, Time>, LineData<Time>>()
        const appendSeriesPoint = (series: LineSeries | undefined) => {
            if (!series?.seriesApi || !series.data?.length) return
            const index = getTimeIndex(series.data, time)
            if (Number.isFinite(index) && series.data[index]) {
                seriesData.set(series.seriesApi, series.data[index])
            }
        }
        Object.values(this.dataManager.seriesApis).forEach(appendSeriesPoint)
        Object.values(this.dataManager.cumSeriesApis).forEach(appendSeriesPoint)
        return seriesData
    }

    onCrosshairMove = (data: MouseEventParams, publish = true) => {
        if (publish) {
            this._syncCrosshairActive = false
        }
        let offset = 0
        const renderedData = this.getRenderedPrimaryData()
        if (!renderedData.length) return
        let index
        if (data.time) {
            index = getTimeIndex(renderedData, data.time as number)
            if (Number.isFinite(index)) {
                offset = renderedData.length - index
            }
        }
        if (offset < 0) offset = 0
        this.currentCrosshairOffset = offset

        let seriesData = new Map<ISeriesApi<SeriesType, Time>, LineData<Time>>()
        if (data.time) {
            seriesData = data.seriesData as Map<ISeriesApi<SeriesType, Time>, LineData<Time>>
        } else {
            Object.values(this.dataManager.seriesApis).forEach((s) => {
                if (s) {
                    const data = s.data
                    if (data) {
                        seriesData.set(s.seriesApi, data[data.length - 1])
                    }
                }
            })
            Object.values(this.dataManager.cumSeriesApis).forEach((s) => {
                if (s) {
                    const data = s.data
                    if (data) {
                        seriesData.set(s.seriesApi, data[data.length - 1])
                    }
                }
            })
        }

        if (data && data.point) {
            const secondaryKey = Object.entries(this.secondarySecurities ?? {})?.[0]?.[1]?.callSecKey
            this.crossHairInfo = {
                point: data.point,
                time: (this.chartApi.timeScale().coordinateToTime(data.point.x) as number) || 0, //data.time as number,
                price: this.dataManager.seriesApis[secondaryKey ?? this.primary]?.seriesApi?.coordinateToPrice(data.point.y) || 0,
                seriesData
            }
        } else {
            this.crossHairInfo = {
                point: undefined as any,
                time: 0 as any,
                price: undefined as any,
                seriesData
            }
        }
        publish && this.callbacks.onCrosshairMove(this.crossHairInfo)
    }

    goToDate(timeInSeconds: number, notifyChart: boolean = false) {
        const zonedTime = convertToTimezone(timeInSeconds, this.timezone)
        const data = this.getRenderedPrimaryData()
        const start = data[0],
            end = data[data.length - 1]
        this.gotoDate = zonedTime
        this.notifyGoToDateToChart = notifyChart

        this.previousViewPortCandleInfo = {
            ...this.viewportCandleInfo,
            startTime: 0,
            endTime: 0
        }
        if ((start && (start.time as number)) > zonedTime || (end && (end.time as number)) < zonedTime) {
            this.fetchData(true)
        } else {
            this.focusTimeInViewPort(zonedTime, data)
        }
    }

    async onVisibleLogicalRangeChange(viewportCandleInfo: ViewportCandleInfo) {
        if (this.destroyed || !this.chartApi) return
        this.setViewportCandleInfo(viewportCandleInfo)
        let callFetchData = true
        while (callFetchData) {
            const numberOfRequests = await this.fetchData(false)
            if (numberOfRequests === 0) {
                callFetchData = false
            }
        }
    }

    subscribeVisibleLogicalRangeChange = throttle(100, (range: LogicalRange | null) => {
        if (this.destroyed || !this.chartApi) return
        const seriesApi = this.dataManager.seriesApis[this.primary]?.seriesApi
        if (!range || !seriesApi) return
        const barsInfo = seriesApi.barsInLogicalRange(range)
        if (!barsInfo) return
        const viewportCandleInfo = getViewportCandleInfo(range, barsInfo, seriesApi.data() as ChartCandle[])
        this.chartApi.timeScale().subscribeVisibleLogicalRangeChange(() => this.onVisibleLogicalRangeChange(viewportCandleInfo))
    })

    onVisibleRangeChange = debounce(100, (range: LogicalRange | null) => {
        if (this.destroyed || !this.chartApi) return
        const seriesApi = this.dataManager?.seriesApis?.[this.primary]?.seriesApi
        if (!range || !seriesApi) return
        const barsInfo = seriesApi.barsInLogicalRange(range)
        if (!barsInfo) return

        const viewportCandleInfo = getViewportCandleInfo(range, barsInfo, seriesApi.data() as ChartCandle[])
        this.onVisibleLogicalRangeChange(viewportCandleInfo)
        this.callbacks.onTimeRangeChange && this.callbacks.onTimeRangeChange(viewportCandleInfo, this.gotoDate)
    })

    getEmptyTimeslotsData(lastPointTimeInUTC: number) {
        const timestamps = getNextChartTimeStamps(istToUTC(lastPointTimeInUTC), this.emptyTimeslotsSize, this.timeBucket, this.holidays, constructCalendarDataLookup(this.tradingCalendarData))
        return timestamps.map((t) => ({ time: zonedTime(t, this.timezone) }))
    }

    updatePrimaryTimesAndEmptyslots() {
        if (!this.timeBucket) return
        const primaryData = this.getRenderedPrimaryData()
        this.primaryTimes = new Set(primaryData.map((pd) => pd.time))
        const lastPoint = primaryData.length ? (primaryData[primaryData.length - 1].time as number) : NaN
        this.emptyTimeSlots = this.getEmptyTimeslotsData(lastPoint)
    }

    removeEmptyTimeSlots() {
        this.emptyTimeSlots = []
    }

    updateLayoutOptions(layoutOptions: ChartLayoutOptions) {
        if (this.destroyed || !this.chartApi) return
        // In embedded mode, ensure native scales remain hidden
        if (this.isEmbedded) {
            const embeddedLayoutOptions = {
                ...layoutOptions,
                leftPriceScale: {
                    ...layoutOptions.leftPriceScale,
                    visible: false
                },
                rightPriceScale: {
                    ...layoutOptions.rightPriceScale,
                    visible: false
                }
            }
            this.chartApi.applyOptions(embeddedLayoutOptions)
        } else {
            this.chartApi.applyOptions(layoutOptions)
        }
        this.layoutOptions = this.chartApi.options()
    }

    getChartSnapshot(): ChartSnapshotMultiStrikeChart {
        return this.crossHairInfo?.seriesData as any
    }

    setSelectedDateRange(dateRange: SuperChartConfiguration_DateRange, temporary: boolean = true) {
        const [timeBucket] = this.timeBuckets.filter((v) => v.code === dateRange.timeBucketCode)
        if (!timeBucket) return
        this.timeBucket = timeBucket
        this.dateRange = dateRange
        this.isDateRangeTemporary = temporary

        this.fetchData(true, true)
    }

    setVisibleLogicalRange(from: number, to: number, reassignHandler: boolean = false) {
        if (this.destroyed || !this.chartApi) return
        if (reassignHandler) this.unsubscribeVisibleLogicalRange()
        const fromTime = this.dataManager.data[this.primary]?.[from]?.time
        const toTime = this.dataManager.data[this.primary]?.[to]?.time
        if (fromTime && toTime) this.setVisibleRange(fromTime, toTime)
        else {
            this.chartApi.timeScale().setVisibleLogicalRange({
                from,
                to
            })
        }
        if (reassignHandler) this.subscribeVisibleLogicalRange()
    }

    setViewportCandleInfo(info: ViewportCandleInfo) {
        this.viewportCandleInfo = info
    }

    resetViewportCandleInfo() {
        this.viewportCandleInfo = {
            barsBefore: 0,
            barsAfter: 0,
            rangeStart: 0,
            rangeEnd: 0,
            startTime: 0,
            endTime: 0,
            viewportCandles: 0
        }
    }

    getRenderedPrimaryData() {
        if (!this.dataManager.data?.[this.primary]) return []
        return this.dataManager.data?.[this.primary]
    }

    // all times are in utc
    focusTimeRange(viewportCandleInfo: ViewportCandleInfo, interval: SuperChartConfiguration_TimeBucket, gotoDate: number | undefined) {
        if (this._mouseEntered) return // to avoid circular updates
        const { startTime, endTime, barsAfter, barsBefore } = viewportCandleInfo
        const timeWidth = getTimeWidthInMilliSeconds(interval)
        const currentTimeWidth = getTimeWidthInMilliSeconds(this.timeBucket)
        const multiplier = timeWidth / currentTimeWidth
        const data = this.getRenderedPrimaryData()
        let startIndex = getTimeIndex(data, startTime)
        let endIndex = getTimeIndex(data, endTime)
        if (Number.isFinite(startIndex) && barsBefore < 0) startIndex += barsBefore * multiplier

        if (Number.isFinite(startIndex) && Number.isFinite(endIndex)) {
            this.setVisibleLogicalRange(startIndex, endIndex)
        } else {
            this.fetchData(false).then((requestCount) => {
                requestCount > 0 && this.focusTimeRange(viewportCandleInfo, interval, gotoDate)
            })
            return
        }
        if (gotoDate !== undefined) {
            this.goToDate(gotoDate)
        }
    }

    setVisibleRange(startTime: Time, endTime: Time) {
        this.chartApi.timeScale().setVisibleRange({
            from: startTime as Time,
            to: endTime as Time
        })
    }

    /** Same window as first load: `INITIAL_CANDLE_COUNT` bars back through future slot 5. */
    private applyInitialVisibleRange(data = this.getRenderedPrimaryData(), fromIndex = Math.max(data.length - INITIAL_CANDLE_COUNT, 0)) {
        if (this.destroyed || !this.chartApi || !data.length) return
        const fromTime = data[fromIndex]?.time
        const toTime = this.emptyTimeSlots?.[INITIAL_VIEWPORT_RIGHT_SLOT]?.time ?? data[data.length - 1].time
        if (fromTime && toTime) {
            this.setVisibleRange(fromTime as Time, toTime as Time)
        }
    }

    gotoCurrentTime() {
        if (this.destroyed || !this.chartApi) return
        const data = this.getRenderedPrimaryData()
        if (!data.length) return
        const latestCandle = data[data.length - 1]

        if (toDayFormat((latestCandle.time as number) * 1000) === toDayFormat(Date.now())) {
            this.applyInitialVisibleRange(data)
        } else {
            this.gotoDate = undefined
            this.fetchData(true)
        }
    }

    focusTimeInViewPort(zonedTimeInSeconds: number, data: SeriesData[]) {
        if (!data || !data.length) return
        const index = getTimeIndex(data as ChartCandle[], zonedTimeInSeconds)
        let from = NaN,
            to = NaN
        const viewportCandles = this.previousViewPortCandleInfo.viewportCandles

        if (this.persistViewport && this.previousViewPortCandleInfo.startTime && this.previousViewPortCandleInfo.endTime) {
            from = getTimeIndex(data as ChartCandle[], this.previousViewPortCandleInfo.startTime) || 0
            to = getTimeIndex(data as ChartCandle[], this.previousViewPortCandleInfo.endTime) || data.length
        } else if (Number.isFinite(index)) {
            from = index - Math.floor(viewportCandles / 2)
            to = index + Math.ceil(viewportCandles / 2)
        }
        if (Number.isFinite(from) && Number.isFinite(to)) {
            this.setVisibleLogicalRange(from, to, true)
        }
        setTimeout(() => {
            // this.notifyGoToDateToChart && this.callbacks.onGotoDateUpdated(data[index].time as number)
            this.notifyGoToDateToChart = false
            this.gotoDate = undefined
        }, 500) // giving 600ms time so that gotodate is updated only after the visible logical range is updated completely
    }

    setCrossHairPosition(price: number, time: number) {
        try {
            if (!this.chartApi) return
            const primarySeries = this.dataManager.seriesApis[this.primary]?.seriesApi
            if (!primarySeries) return
            const data = this.getRenderedPrimaryData()
            if (!data || !data.length) return
            const index = getTimeIndex(data as ChartCandle[], time)
            if (Number.isFinite(index) && data[index]) {
                const candleTime = data[index].time
                const primaryValue = (data[index] as ChartCandle & Partial<LineData<Time>>).value
                const syncPrice = Number.isFinite(primaryValue) ? (primaryValue as number) : price
                const x = this.chartApi.timeScale().timeToCoordinate(candleTime)
                const y = primarySeries.priceToCoordinate(syncPrice)
                if (x == null || y == null) return
                this._syncCrosshairActive = true
                this.chartApi.setCrosshairPosition(syncPrice, candleTime, primarySeries)
                this.onCrosshairMove(
                    {
                        seriesData: this.buildSeriesDataAtTime(candleTime as number),
                        point: { x, y },
                        time: candleTime
                    },
                    false
                )
                this.callbacks.onCrosshairMove(this.crossHairInfo)
                this.publishMetadataCrosshair()
            } else {
                this.clearSyncedCrosshair()
            }
        } catch (e) {
            // Chart may be destroyed or in invalid state when receiving crosshair sync from other widgets
        }
    }

    clearSyncedCrosshair() {
        this._syncCrosshairActive = false
        if (this.chartApi) {
            this.chartApi.clearCrosshairPosition()
        }
        this.onCrosshairMove({} as any, false)
        this.callbacks.onCrosshairMove(this.crossHairInfo)
        this.callbacks.onDataUpdate()
    }

    onMouseEnter = () => {
        this._syncCrosshairActive = false
        this._mouseEntered = true
    }

    onMouseMove = (event: any) => {
        if (!this.containerBounds) {
            this.containerBounds = this.container.getBoundingClientRect()
        }
        const { left, right, top, bottom } = this.containerBounds
        const x = event.clientX,
            y = event.clientY
        const withinBounds = x > left && x < right && y > top && y < bottom
        this.hoverActive = withinBounds
        if (!withinBounds) {
            this.onMouseLeave()
        } else {
            if (!this._mouseEntered) {
                this._mouseEntered = true
                this.callbacks.onMouseEnter && this.callbacks.onMouseEnter()
            }
            this.callbacks.onMouseMove && this.callbacks.onMouseMove()
        }
    }

    onMouseLeave = () => {
        this.callbacks.onMouseLeave && this.callbacks.onMouseLeave()
        this._mouseEntered = false
        if (this._syncCrosshairActive) return
        if (this.chartApi) {
            this.chartApi.clearCrosshairPosition()
        }
        this.onCrosshairMove({} as any)
        this.callbacks.onDataUpdate()
    }

    onBlur() {
        this.setFocused(false)
    }

    getLeftScaleWidth(): number {
        if (this.destroyed || !this.chartApi || this.isEmbedded) return 0
        try {
            const width = this.chartApi.priceScale('left').width()
            return width > 0 ? width : 0
        } catch {
            return 0
        }
    }

    destroy() {
        if (this.destroyed) return
        this.destroyed = true
        this.resizeObserver.unobserve(this.container)
        this.resizeObserver.disconnect()
        this.dataManager.destroy()
        document.removeEventListener('mouseleave', this.onMouseLeave)
        try {
            this.chartApi?.unsubscribeCrosshairMove(this.onCrosshairMove)
            this.chartApi?.remove()
        } catch {
            // Chart may already be disposed or in invalid state during unmount
        }
        ;(this as any).chartApi = null
    }
}